import { DEFAULT_FLOOR_PLAN } from '../../utils/floorPlanGeometry.logic';
import React, { useEffect, useRef, useCallback } from 'react';
import { Stage, Layer } from 'react-konva';
import Konva from 'konva';
import { useShallow } from 'zustand/react/shallow';
import { useEditorStore, CELL, PPM, RackNode } from '../../store/editorStore';
import { createFloorEntityDefaults } from '../../utils/floorEntities.logic';
import { snapToGrid } from '../../utils/cadSnapping.logic';
import { findNearestFreePosition, isPositionOccupied, Box2D } from '../../utils/collisionGuard.logic';
import { getTrayMediaConfig } from '../../utils/traySegregation.logic';
import { GridLayer } from './GridLayer';
import { RacksLayer } from './RacksLayer';
import { TraysLayer } from './TraysLayer';
import { LensLayer } from './LensLayer';
import { OverlayLayer } from './OverlayLayer';
import { TooltipOverlay } from './TooltipOverlay';
import { LensLegend } from './LensLegend';
import { ElevationPanel } from './ElevationPanel';
import { useCanvasControls } from '../../hooks/useCanvasControls';
import { AislesLayer } from './AislesLayer';
import { OperationalZonesLayer } from './OperationalZonesLayer';
import { StructuralGeometryLayer } from './StructuralGeometryLayer';
import type { AisleType } from '../../store/editorStore';
import { useTelemetryStream } from '../../hooks/useTelemetryStream';
import { structuralGeometryToBoxes, validateStructuralPlacement } from '../../utils/structuralGeometry.logic';
import type { StructuralGeometryKind } from '../../utils/structuralGeometry.logic';
import { buildFloorPlanProjection } from '../../utils/floorPlanProjection.logic';
import type { EditorCommandId } from '../../utils/editorCommands.logic';
import { BackgroundUnderlay } from './BackgroundUnderlay';
import { MeasurementOverlay } from './MeasurementOverlay';
import { FloorPlanningLayer, FloorReservationsList } from './FloorPlanningLayer';
import { placementFromSource } from '../../utils/placement.logic';
import { formatRowRackLabel } from '../../store/editorStore';
import { appendMeasurementPoint, calculateMeasurementLength, decodeMeasurement, encodeMeasurement, formatMeasurementLength, snapMeasurementPoint } from '../../utils/measurement.logic';
import type { Measurement, MeasurementPoint } from '../../utils/measurement.logic';

interface DataCenterCanvasProps {
  dcCode?: string;
  stageRef?: React.RefObject<any>;
  executeCommand?: (id: EditorCommandId) => void;
  onContextMenu?: (point: { x: number; y: number }) => void;
}

function genId() {
  return Math.random().toString(36).slice(2, 10);
}

export const DataCenterCanvas: React.FC<DataCenterCanvasProps> = ({
  dcCode,
  stageRef: externalStageRef,
  executeCommand,
  onContextMenu,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const internalStageRef = useRef<any>(null);
  const stageRef = externalStageRef || internalStageRef;
  const derivedAisleSignature = useRef<string | null>(null);

  const {
    stageX, stageY, stageScale,
    stageW, stageH,
    clearSelection,
    tool,
    measurementMode,
    selectedIds, setSelectedIds,
    racks,
    moveRacks,
    setMarquee,
    addTrayNode, addTrayEdge,
    drawingTrayFromId, setDrawingTrayFromId,
    addAisleZone,
    addStructuralGeometry,
    activeLens,
    setTelemetryData,
    setCollidingIds,
    floorPlan,
    structuredGeometry,
    rows,
    aisleZones,
    recomputeDerivedAisles,
    saveStatus,
    trayNodes,
    trayEdges,
  } = useEditorStore(useShallow(state => ({
    stageX: state.stageX, stageY: state.stageY, stageScale: state.stageScale,
    stageW: state.stageW, stageH: state.stageH,
    clearSelection: state.clearSelection,
    tool: state.tool,
    measurementMode: state.measurementMode,
    selectedIds: state.selectedIds, setSelectedIds: state.setSelectedIds,
    racks: state.racks,
    moveRacks: state.moveRacks,
    setMarquee: state.setMarquee,
    addTrayNode: state.addTrayNode, addTrayEdge: state.addTrayEdge,
    drawingTrayFromId: state.drawingTrayFromId, setDrawingTrayFromId: state.setDrawingTrayFromId,
    addAisleZone: state.addAisleZone,
    addStructuralGeometry: state.addStructuralGeometry,
    activeLens: state.activeLens,
    setTelemetryData: state.setTelemetryData,
    setCollidingIds: state.setCollidingIds,
    floorPlan: state.dcSettings.floorPlan ?? DEFAULT_FLOOR_PLAN,
    structuredGeometry: state.dcSettings.structuredGeometry,
    rows: state.rows,
    aisleZones: state.aisleZones,
    recomputeDerivedAisles: state.recomputeDerivedAisles,
    saveStatus: state.saveStatus,
    trayNodes: state.trayNodes,
    trayEdges: state.trayEdges,
  })));

  const workerRef = useRef<Worker | null>(null);
  const projection = React.useMemo(() => buildFloorPlanProjection({
    floorPlan,
    racks: Object.values(racks),
    rows: Object.values(rows),
    aisleZones: Object.values(aisleZones),
    structuralGeometry: structuredGeometry,
    pathways: { nodes: Object.values(trayNodes), edges: Object.values(trayEdges) },
  }), [floorPlan, racks, rows, aisleZones, trayNodes, trayEdges, structuredGeometry]);

  // WebWorker for collision detection: instantiate once
  useEffect(() => {
    workerRef.current = new Worker(
      new URL('../../workers/collisionWorker.ts', import.meta.url),
      { type: 'module' }
    );
    
    workerRef.current.onmessage = (e) => {
      setCollidingIds(e.data.collidingIds || []);
    };

    return () => {
      workerRef.current?.terminate();
    };
  }, [setCollidingIds]);

  // Post updates to worker when racks configuration changes
  useEffect(() => {
    if (!workerRef.current) return;
    const rackList = projection.racks.map(r => ({
      id: r.id,
      x: r.placement.xM * PPM,
      y: r.placement.yM * PPM,
      w: r.placement.widthM * PPM,
      h: r.placement.depthM * PPM,
      rotation: r.placement.orientation,
    }));

    workerRef.current.postMessage({ racks: rackList });
  }, [projection]);

  useEffect(() => {
    const signature = JSON.stringify({ rows, racks: Object.values(racks).map(rack => ({
      id: rack.id, rowId: rack.rowId, x: rack.x, y: rack.y, width: rack.width, height: rack.height,
    })) });
    if (derivedAisleSignature.current === null) {
      derivedAisleSignature.current = signature;
      return;
    }
    if (derivedAisleSignature.current === signature) return;
    derivedAisleSignature.current = signature;
    // Loading published/draft rows changes the projection but is not an edit.
    // Only recompute after the store has entered a user-editable dirty state.
    if (saveStatus === 'idle' || saveStatus === 'saved') return;
    recomputeDerivedAisles();
  }, [rows, racks, recomputeDerivedAisles, saveStatus]);

  // Telemetry real-time sync when a lens is active
  const isLensActive = activeLens !== 'none';
  const liveMetrics = useTelemetryStream(isLensActive ? dcCode : undefined, undefined);

  useEffect(() => {
    if (isLensActive && liveMetrics && Object.keys(liveMetrics).length > 0) {
      setTelemetryData(liveMetrics);
    }
  }, [liveMetrics, isLensActive, setTelemetryData]);

  // Zone drawing state (ephemeral, not in store)
  const zoneDrawStart = useRef<{ x: number; y: number } | null>(null);
  const [zoneDraft, setZoneDraft] = React.useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [measurement, setMeasurement] = React.useState<Measurement | null>(() => decodeMeasurement(new URLSearchParams(window.location.search).get('measure')));
  const [measurementPreview, setMeasurementPreview] = React.useState<{ x: number; y: number } | null>(null);
  const measurementDraft = useRef<MeasurementPoint[]>([]);
  const pendingZoneType = useRef<AisleType>('cold');
  const {
    handleWheel,
    handleStageDragEnd,
    screenToWorld,
    ghostPos,
    setGhostPos,
    marqueeStart,
    isDraggingMarquee,
  } = useCanvasControls(containerRef, executeCommand);

  const handleRackContextMenu = useCallback((rackId: string, x: number, y: number) => {
    const state = useEditorStore.getState();
    if (!state.selectedIds.includes(rackId)) state.setSelectedIds([rackId]);
    onContextMenu?.({ x, y });
  }, [onContextMenu]);

  const updateMeasurementUrl = useCallback((next: Measurement | null) => {
    const url = new URL(window.location.href);
    if (next) url.searchParams.set('measure', encodeMeasurement(next));
    else url.searchParams.delete('measure');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }, []);

  const addMeasurementPoint = useCallback((raw: { x: number; y: number }, id?: string, source?: MeasurementPoint['source']) => {
    const state = useEditorStore.getState();
    const floorPlan = state.dcSettings.floorPlan ?? DEFAULT_FLOOR_PLAN;
    const candidates: MeasurementPoint[] = [
      ...Object.values(state.trayNodes).map(node => ({ x: node.x, y: node.y, source: 'pathway' as const, id: node.id })),
      ...Object.values(state.racks).map(rack => ({ x: rack.x + (rack.widthM || 0.6) * PPM / 2, y: rack.y + (rack.depthM || 1) * PPM / 2, source: 'entry' as const, id: rack.id })),
    ];
    const point = id && source ? { ...raw, id, source } : snapMeasurementPoint(raw, {
      tilePx: floorPlan.tileSizeMm / 1000 * PPM,
      originX: floorPlan.originXMm / 1000 * PPM,
      originY: floorPlan.originYMm / 1000 * PPM,
    }, candidates);
    const points = appendMeasurementPoint({ mode: measurementMode, points: measurementDraft.current }, point);
    measurementDraft.current = points;
    if (measurementMode === 'direct' && points.length >= 2) {
      const next = { mode: 'direct' as const, points: points.slice(-2) };
      setMeasurement(next);
      updateMeasurementUrl(next);
      measurementDraft.current = [];
      setMeasurementPreview(null);
    } else {
      setMeasurement({ mode: measurementMode, points });
      setMeasurementPreview(null);
    }
  }, [measurementMode, updateMeasurementUrl]);

  const finishOrthogonalMeasurement = useCallback(() => {
    if (measurementMode !== 'orthogonal' || measurementDraft.current.length < 2) return;
    const next = { mode: 'orthogonal' as const, points: [...measurementDraft.current] };
    setMeasurement(next);
    updateMeasurementUrl(next);
    measurementDraft.current = [];
    setMeasurementPreview(null);
  }, [measurementMode, updateMeasurementUrl]);

  const clearMeasurement = useCallback(() => {
    measurementDraft.current = [];
    setMeasurement(null);
    setMeasurementPreview(null);
    updateMeasurementUrl(null);
  }, [updateMeasurementUrl]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Enter') finishOrthogonalMeasurement();
      if (event.key === 'Escape' && tool === 'measure') clearMeasurement();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [tool, finishOrthogonalMeasurement, clearMeasurement]);


  // ── Mouse down on stage (marquee / tray / erase) ─────────
  const handleStageMouseDown = useCallback((e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (e.target !== e.target.getStage()) return; // hit a shape, not empty stage

    if (tool === 'select') {
      const ptr = e.target.getStage()?.getPointerPosition();
      if (!ptr) return;
      const w = screenToWorld(ptr.x, ptr.y);
      marqueeStart.current = w;
      isDraggingMarquee.current = false;
    }

    if (tool === 'draw_zone') {
      const ptr = e.target.getStage()?.getPointerPosition();
      if (!ptr) return;
      const w = screenToWorld(ptr.x, ptr.y);
      zoneDrawStart.current = { x: w.x, y: w.y };
      setZoneDraft({ x: w.x, y: w.y, w: 0, h: 0 });
    }

    if (tool === 'draw_tray') {
      // Place or connect tray node on empty stage click
      const stage = e.target.getStage();
      const ptr = stage?.getPointerPosition();
      if (!ptr) return;
      const w = screenToWorld(ptr.x, ptr.y);
      let snappedX = Math.round(w.x / 20) * 20;
      let snappedY = Math.round(w.y / 20) * 20;
      
      const state = useEditorStore.getState();
      const fromNode = drawingTrayFromId ? state.trayNodes[drawingTrayFromId] : null;
      const SNAP_DIST = 15;

      // 1. Magnetic Snapping to existing Tray Nodes (prioritize reusing nodes)
      let targetNodeId: string | null = null;
      for (const n of Object.values(state.trayNodes)) {
        if (Math.hypot(w.x - n.x, w.y - n.y) < SNAP_DIST) {
          snappedX = n.x;
          snappedY = n.y;
          targetNodeId = n.id;
          break;
        }
      }

      // 2. Magnetic Snapping to Rack Centers if no existing tray node
      if (!targetNodeId) {
        for (const r of Object.values(state.racks)) {
          const rw = (r.widthM || 0.6) * 100;
          const rh = (r.depthM || 1.0) * 100;
          const cx = r.x + rw / 2;
          const cy = r.y + rh / 2;
          if (Math.abs(w.x - cx) < SNAP_DIST) snappedX = cx;
          if (Math.abs(w.y - cy) < SNAP_DIST) snappedY = cy;
        }
      }

      // 3. Ortho Mode (Shift key)
      if (e.evt.shiftKey && fromNode) {
        if (Math.abs(w.x - fromNode.x) > Math.abs(w.y - fromNode.y)) {
          snappedY = fromNode.y;
        } else {
          snappedX = fromNode.x;
        }
      }
      
      const fromId = drawingTrayFromId;
      const effectiveNodeId = targetNodeId || genId();
      if (!targetNodeId) {
        addTrayNode({ id: effectiveNodeId, x: snappedX, y: snappedY, type: 'terminal' });
      }

      if (fromId && fromId !== effectiveNodeId) {
        const media = useEditorStore.getState().trayDrawType;
        const config = getTrayMediaConfig(media);
        addTrayEdge({
          id: genId(),
          source: fromId,
          target: effectiveNodeId,
          color: config.color,
          type: media,
          mediaType: media,
          trayType: config.defaultType,
          widthM: 0.3,
        });
      }
      setDrawingTrayFromId(effectiveNodeId);
      setGhostPos({ x: snappedX, y: snappedY });
    }

    if (tool === 'measure') {
      const stage = e.target.getStage();
      const ptr = stage?.getPointerPosition();
      if (ptr) addMeasurementPoint(screenToWorld(ptr.x, ptr.y));
    }
  }, [tool, screenToWorld, drawingTrayFromId, addTrayNode, addTrayEdge, setDrawingTrayFromId, setGhostPos, addMeasurementPoint]);

  // ── Mouse move (ghost line + marquee rect) ───────────────
  const handleStageMouseMove = useCallback((e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    const ptr = e.target.getStage()?.getPointerPosition();
    if (!ptr) return;
    const w = screenToWorld(ptr.x, ptr.y);

    if (tool === 'draw_tray' && drawingTrayFromId) {
      let snapX = Math.round(w.x / 20) * 20;
      let snapY = Math.round(w.y / 20) * 20;
      
      const state = useEditorStore.getState();
      const fromNode = state.trayNodes[drawingTrayFromId];
      const SNAP_DIST = 15;
      
      // 1. Magnetic Snapping to existing Tray Nodes
      let snappedToNode = false;
      for (const n of Object.values(state.trayNodes)) {
        if (Math.hypot(w.x - n.x, w.y - n.y) < SNAP_DIST) {
          snapX = n.x;
          snapY = n.y;
          snappedToNode = true;
          break;
        }
      }

      // 2. Magnetic Snapping to Rack Centers (if not snapped to a node)
      if (!snappedToNode) {
        for (const r of Object.values(state.racks)) {
          const rw = (r.widthM || 0.6) * 100;
          const rh = (r.depthM || 1.0) * 100;
          const cx = r.x + rw / 2;
          const cy = r.y + rh / 2;
          if (Math.abs(w.x - cx) < SNAP_DIST) snapX = cx;
          if (Math.abs(w.y - cy) < SNAP_DIST) snapY = cy;
        }
      }
      
      // 3. Ortho Mode (Shift key)
      if (e.evt.shiftKey && fromNode) {
        if (Math.abs(w.x - fromNode.x) > Math.abs(w.y - fromNode.y)) {
          snapY = fromNode.y;
        } else {
          snapX = fromNode.x;
        }
      }
      
      setGhostPos({ x: snapX, y: snapY });
    }

    if (tool === 'measure' && measurementDraft.current.length > 0) {
      const floorPlan = useEditorStore.getState().dcSettings.floorPlan ?? DEFAULT_FLOOR_PLAN;
      const candidates: MeasurementPoint[] = [
        ...Object.values(trayNodes).map(node => ({ x: node.x, y: node.y, source: 'pathway' as const, id: node.id })),
        ...Object.values(racks).map(rack => ({ x: rack.x + (rack.widthM || 0.6) * PPM / 2, y: rack.y + (rack.depthM || 1) * PPM / 2, source: 'entry' as const, id: rack.id })),
      ];
      setMeasurementPreview(snapMeasurementPoint(w, {
        tilePx: floorPlan.tileSizeMm / 1000 * PPM,
        originX: floorPlan.originXMm / 1000 * PPM,
        originY: floorPlan.originYMm / 1000 * PPM,
      }, candidates));
    }

    // Zone drawing preview
    if (tool === 'draw_zone' && zoneDrawStart.current) {
      const sx = zoneDrawStart.current.x;
      const sy = zoneDrawStart.current.y;
      setZoneDraft({
        x: Math.min(sx, w.x),
        y: Math.min(sy, w.y),
        w: Math.abs(w.x - sx),
        h: Math.abs(w.y - sy),
      });
    }

    if (tool === 'select' && marqueeStart.current) {
      isDraggingMarquee.current = true;
      const mx = Math.min(marqueeStart.current.x, w.x);
      const my = Math.min(marqueeStart.current.y, w.y);
      const mw = Math.abs(w.x - marqueeStart.current.x);
      const mh = Math.abs(w.y - marqueeStart.current.y);
      if (mw > 4 || mh > 4) {
        setMarquee({ x: mx, y: my, w: mw, h: mh });
      }
    }
  }, [tool, drawingTrayFromId, screenToWorld, setGhostPos, setMarquee, trayNodes, racks]);

  // ── Mouse up (finish marquee select) ────────────────────
  const handleStageMouseUp = useCallback((e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (tool === 'select' && isDraggingMarquee.current && marqueeStart.current) {
      const stage = e.target.getStage();
      const ptr = stage?.getPointerPosition();
      if (!ptr) return;
      const w = screenToWorld(ptr.x, ptr.y);
      const rx = Math.min(marqueeStart.current.x, w.x);
      const ry = Math.min(marqueeStart.current.y, w.y);
      const rw = Math.abs(w.x - marqueeStart.current.x);
      const rh = Math.abs(w.y - marqueeStart.current.y);

      const RACK_W = (rack: RackNode) => (rack.widthM || 0.6) * 100;
      const RACK_H = (rack: RackNode) => (rack.depthM || 1.0) * 100;
      const hits = Object.values(racks).filter(rack =>
        rack.x < rx + rw &&
        rack.x + RACK_W(rack) > rx &&
        rack.y < ry + rh &&
        rack.y + RACK_H(rack) > ry
      );
      setSelectedIds(hits.map(r => r.id));
      setMarquee(null);
    }

    // Finish zone draw
    if (tool === 'draw_zone' && zoneDraft && zoneDraft.w > 20 && zoneDraft.h > 20) {
      const id = genId();
      addAisleZone({
        id,
        x: zoneDraft.x,
        y: zoneDraft.y,
        w: zoneDraft.w,
        h: zoneDraft.h,
        aisleType: pendingZoneType.current,
        isDirty: true,
      });
    }
    zoneDrawStart.current = null;
    setZoneDraft(null);

    marqueeStart.current = null;
    isDraggingMarquee.current = false;
  }, [tool, screenToWorld, racks, setSelectedIds, setMarquee, zoneDraft, addAisleZone]);

  // ── Click on empty stage (deselect) ─────────────────────
  const handleStageClick = useCallback((e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (tool === 'measure') return;
    if (e.target !== e.target.getStage()) return;
    if (!isDraggingMarquee.current) {
      clearSelection();
    }
  }, [clearSelection, tool]);

  const handleMeasurementDoubleClick = useCallback(() => finishOrthogonalMeasurement(), [finishOrthogonalMeasurement]);

  // ── Drop from ObjectPalette ──────────────────────────────
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!stageRef.current) return;

    const rect = (containerRef.current as HTMLElement).getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const w = screenToWorld(sx, sy);
    const gridPx = floorPlan.tileSizeMm / 1000 * PPM;
    const originX = floorPlan.originXMm / 1000 * PPM;
    const originY = floorPlan.originYMm / 1000 * PPM;
    const snapX = snapToGrid(w.x, gridPx, originX);
    const snapY = snapToGrid(w.y, gridPx, originY);

    // Zone drop
    const zoneType = e.dataTransfer.getData('zoneType') as AisleType | '';
    if (zoneType) {
      const ZONE_W = CELL * 3;
      const ZONE_H = CELL * 2;
      addAisleZone({
        id: genId(),
        x: snapX - ZONE_W / 2,
        y: snapY - ZONE_H / 2,
        w: ZONE_W,
        h: ZONE_H,
        aisleType: zoneType,
        isDirty: true,
      });
      return;
    }

    const structureKind = e.dataTransfer.getData('structureKind') as Exclude<StructuralGeometryKind, 'boundary'> | '';
    if (structureKind) {
      const defaults: Record<Exclude<StructuralGeometryKind, 'boundary'>, { widthM: number; depthM: number; blocksPlacement: boolean }> = {
        wall: { widthM: 2, depthM: 0.15, blocksPlacement: true },
        door: { widthM: 1, depthM: 0.15, blocksPlacement: true },
        column: { widthM: 0.6, depthM: 0.6, blocksPlacement: true },
        restricted_zone: { widthM: 3, depthM: 3, blocksPlacement: true },
      };
      const preset = defaults[structureKind];
      const id = `structure-${genId()}`;
      addStructuralGeometry({
        id,
        kind: structureKind,
        xM: Math.max(0, (snapX - preset.widthM * PPM / 2) / PPM),
        yM: Math.max(0, (snapY - preset.depthM * PPM / 2) / PPM),
        widthM: preset.widthM,
        depthM: preset.depthM,
        rotation: 0,
        blocksPlacement: preset.blocksPlacement,
        blocksRouting: structureKind !== 'door',
      });
      setSelectedIds([id]);
      return;
    }

    // Floor Entity drop (IT Rack, Facility Unit, Pillar)
    const unplacedRackIds = e.dataTransfer.getData('unplacedRackIds');
    if (unplacedRackIds) {
      const selectedRacks = unplacedRackIds.split(',').map(Number).filter(Number.isInteger).map(dbId =>
        Object.values(racks).find(item => item.dbId === dbId),
      ).filter((rack): rack is RackNode => Boolean(rack));
      if (!selectedRacks.length) return;
      const selectedIds = new Set(selectedRacks.map(rack => rack.id));
      const existingBoxes: Box2D[] = Object.values(racks).filter(item => !selectedIds.has(item.id)).map(item => ({
        id: item.id, x: item.x, y: item.y, w: (item.widthM || 0.6) * PPM, h: (item.depthM || 1.0) * PPM, rotation: item.rotation || 0,
      }));
      const moves: Array<{ id: string; x: number; y: number }> = [];
      const placedBoxes = [...existingBoxes];
      selectedRacks.forEach((rack, index) => {
        const candidateBox: Box2D = { id: rack.id, x: snapX + index * gridPx, y: snapY, w: rack.width || 60, h: rack.height || 100, rotation: rack.rotation || 0 };
        const placedPos = findNearestFreePosition(candidateBox, placedBoxes, gridPx, 6, structuralGeometryToBoxes(structuredGeometry, PPM));
        if (!isPositionOccupied({ ...candidateBox, x: placedPos.x, y: placedPos.y }, placedBoxes)) {
          moves.push({ id: rack.id, x: placedPos.x, y: placedPos.y });
          placedBoxes.push({ ...candidateBox, x: placedPos.x, y: placedPos.y });
        }
      });
      if (moves.length) {
        moveRacks(moves);
        setSelectedIds(moves.map(move => move.id));
      }
      return;
    }

    const rackType = e.dataTransfer.getData('rackType');
    if (!rackType) return;

    const defaults = createFloorEntityDefaults(rackType);
    const seq = useEditorStore.getState()._paletteSeq;
    const id = genId();
    const label = `${defaults.label} · без адреса`;
    const candW = Math.round(defaults.width_m * PPM);
    const candH = Math.round(defaults.depth_m * PPM);

    const existingBoxes: Box2D[] = Object.values(racks).map(r => ({
      id: r.id,
      x: r.x,
      y: r.y,
      w: (r.widthM || 0.6) * PPM,
      h: (r.depthM || 1.0) * PPM,
      rotation: r.rotation || 0,
    }));

    const candidateBox: Box2D = {
      id,
      x: snapX,
      y: snapY,
      w: candW,
      h: candH,
      rotation: 0,
    };

    // Collision Guard: if dropped onto an occupied tile, automatically find the nearest free tile
    const placedPos = findNearestFreePosition(
      candidateBox,
      existingBoxes,
      gridPx,
      6,
      structuralGeometryToBoxes(structuredGeometry, PPM),
    );
    const placementCheck = validateStructuralPlacement(
      { ...candidateBox, x: placedPos.x, y: placedPos.y },
      structuredGeometry,
      {
        widthM: floorPlan.widthMm / 1000,
        heightM: floorPlan.heightMm / 1000,
        originXM: floorPlan.originXMm / 1000,
        originYM: floorPlan.originYMm / 1000,
        ppm: PPM,
      },
    );
    if (!placementCheck.valid || isPositionOccupied({ ...candidateBox, x: placedPos.x, y: placedPos.y }, existingBoxes)) return;

    useEditorStore.getState().addRack({
      id,
      x: placedPos.x,
      y: placedPos.y,
      width: candW,
      height: candH,
      rotation: 0,
      orientation: 0,
      label,
      rackType: rackType as any,
      uCount: defaults.u_slots,
      isFacility: defaults.is_facility,
      isBlocked: defaults.is_blocked,
      widthM: defaults.width_m,
      depthM: defaults.depth_m,
      heightM: defaults.height_m,
      tenant: 'mine',
      isDirty: true,
      rowIndex: 10000,
      rackIndex: seq,
    });
    setSelectedIds([id]);
  }, [screenToWorld, setSelectedIds, addAisleZone, addStructuralGeometry, racks, floorPlan, structuredGeometry, moveRacks]);


  const isDraggingStage = tool === 'select' && selectedIds.length === 0;
  const showPlanning = useEditorStore(state => state.showPlanning);
  const planning = useEditorStore(state => state.dcSettings.planning) ?? { placements: [], rack_reservations: [] };
  const planningSettings = useEditorStore(state => state.dcSettings);

  const showElevation = useEditorStore(useShallow(state => state.showElevation));
  const selectedRack = selectedIds.length === 1 ? racks[selectedIds[0]] : undefined;
  const rackCount = Object.values(racks).filter(rack => !rack.isFacility && !rack.isBlocked).length;
  const unassignedRackCount = Object.values(racks).filter(rack => (
    !rack.isFacility && !rack.isBlocked && (!rack.rowId || !Number.isInteger(rack.rackPosInRow))
  )).length;

  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column',
        width: '100%', height: '100%',
        background: '#0d1117',
        overflow: 'hidden',
      }}
    >
      {/* Main canvas area */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          cursor: tool === 'draw_tray' || tool === 'draw_zone' || tool === 'measure' ? 'crosshair' : tool === 'erase' ? 'not-allowed' : 'default',
        }}
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
      >
        <BackgroundUnderlay />
        {stageW > 0 && stageH > 0 && (
          <Stage
            ref={stageRef}
            width={stageW}
            height={stageH}
            x={stageX}
            y={stageY}
            scaleX={stageScale}
            scaleY={stageScale}
            draggable={isDraggingStage}
            onWheel={handleWheel}
            onDragEnd={handleStageDragEnd}
            onClick={handleStageClick}
            onDblClick={handleMeasurementDoubleClick}
            onMouseDown={handleStageMouseDown}
            onMouseMove={handleStageMouseMove}
            onMouseUp={handleStageMouseUp}
            onContextMenu={e => {
              e.evt.preventDefault();
              if (drawingTrayFromId) {
                setDrawingTrayFromId(null);
                setGhostPos(null);
              }
              onContextMenu?.({ x: e.evt.clientX, y: e.evt.clientY });
            }}
          >
            <Layer>
              <OperationalZonesLayer />
              <AislesLayer />
              <GridLayer />
              <StructuralGeometryLayer />
              <TraysLayer ghostPos={ghostPos} dcCode={dcCode || ''} onMeasurePoint={(point, id) => addMeasurementPoint(point, id, 'pathway')} />
              <RacksLayer onContextMenu={handleRackContextMenu} executeCommand={executeCommand} onMeasurePoint={(point, id) => addMeasurementPoint(point, id, 'entry')} />
              <LensLayer />
              <OverlayLayer />
              <MeasurementOverlay measurement={measurement} previewPoint={tool === 'measure' ? measurementPreview : null} stageScale={stageScale} />
            </Layer>
          </Stage>
        )}

        {showPlanning && <>
          <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            <g transform={`translate(${stageX},${stageY}) scale(${stageScale})`}>
              <FloorPlanningLayer planning={planning} ppm={PPM} zoom={stageScale}
                racks={Object.values(racks).filter(r => r.dbId).map(r => ({ ...placementFromSource(r), id: r.dbId!, label: r.label }))}
                targetLabel={(rowId, position) => rows[rowId] ? formatRowRackLabel(planningSettings, rows[rowId], position) : `Место ${position}`}
                onSelect={id => { const rack = Object.values(racks).find(r => r.dbId === id); if (rack) setSelectedIds([rack.id]); }} />
            </g>
          </svg>
          {dcCode && <FloorReservationsList code={dcCode} planning={planning} />}
        </>}
        <TooltipOverlay stageRef={stageRef} />
        <LensLegend />
        <div className="editor-scene-hud" aria-label="Состояние рабочей сцены">
          <div className="editor-scene-hud-title">
            <span>РАБОЧАЯ СЦЕНА</span>
            <strong>{tool === 'select' ? 'Выбор' : tool === 'draw_tray' ? 'Магистраль' : tool === 'measure' ? 'Измерение' : tool === 'draw_zone' ? 'Зона' : 'Удаление'}</strong>
          </div>
          <div className="editor-scene-hud-stats">
            <span><b>{rackCount}</b> стоек</span>
            <span><b>{Object.keys(rows).length}</b> рядов</span>
            <span><b>{Object.keys(trayEdges).length}</b> сегм.</span>
            {unassignedRackCount > 0 && <span className="editor-scene-hud-warning"><b>{unassignedRackCount}</b> без адреса</span>}
          </div>
          {selectedRack && (
            <div className="editor-scene-hud-selection">
              <span>Выбрано</span>
              <strong>{selectedRack.label}</strong>
            </div>
          )}
        </div>
        {Object.keys(trayEdges).length > 0 && (
          <div className="editor-pathway-key" aria-label="Легенда магистралей">
            <span className="editor-pathway-key-title">МАГИСТРАЛИ</span>
            <span><i className="editor-pathway-swatch is-copper" /> Медь</span>
            <span><i className="editor-pathway-swatch is-fiber" /> Оптика</span>
            <span><i className="editor-pathway-swatch is-power" /> Питание</span>
          </div>
        )}
        {measurement && (
          <div className="editor-measurement-readout" aria-live="polite" style={{ position: 'absolute', left: 12, bottom: 12, zIndex: 4, display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', border: '1px solid rgba(245,158,11,.45)', borderRadius: 6, background: 'rgba(10,15,24,.92)', color: '#f8fafc', fontSize: 12, boxShadow: '0 6px 18px rgba(0,0,0,.22)', pointerEvents: 'none' }}>
            <strong>{measurement.mode === 'direct' ? 'Прямая' : 'Ортогональная'}</strong>
            <span>{formatMeasurementLength(calculateMeasurementLength(measurement))}</span>
            {measurement.mode === 'direct' && <span style={{ color: '#fbbf24' }}>не маршрут</span>}
          </div>
        )}
      </div>

      {/* Z-view elevation panel */}
      {showElevation && <ElevationPanel height={160} />}
    </div>
  );
};
